/**
 * Reports — 8 relatórios core (SVG charts inline, sem deps externas).
 * Spec: specs/MIGRATION-COCKPIT/09-reports.md
 */
import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api.js';
import { useTableProfile } from '../../hooks/useTableProfile.js';
import { DataTable } from '@shared/UI/DataTable';
import '@shared/UI/DataTable/DataTable.css';

const eur = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const num = (n) => new Intl.NumberFormat('pt-PT').format(n || 0);

const REPORTS = [
  { id: 'kpis', name: 'KPIs Gerais', icon: '📊' },
  { id: 'orders-timeline', name: 'Timeline', icon: '📈' },
  { id: 'orders-by-status', name: 'Por Estado', icon: '📋' },
  { id: 'top-customers', name: 'Top Clientes', icon: '👥' },
  { id: 'top-products', name: 'Top Produtos', icon: '💊' },
  { id: 'pendentes', name: 'Pendentes', icon: '⏳' },
  { id: 'sales-vs-sap', name: 'Sales vs SAP', icon: '🔗' },
  { id: 'quotations', name: 'Cotações LC/LN', icon: '📑' },
];

const Reports = () => {
  const [active, setActive] = useState('kpis');
  const [filters, setFilters] = useState({
    start_date: '', end_date: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const r = await api.get(`/reports/${active}`, { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [active]);

  return (
    <div className="gencrm_cockpit_reports">
      <div className="gencrm_page-tabs">
        {REPORTS.map((r) => (
          <button
            key={r.id}
            onClick={() => setActive(r.id)}
            className={`gencrm_page-tabs__tab ${active === r.id ? 'gencrm_page-tabs__tab--active' : ''}`}
          >
            {r.icon && <span className="gencrm_page-tabs__icon">{r.icon}</span>}
            <span>{r.name}</span>
          </button>
        ))}
      </div>

      <div className="gencrm_reports__content">
        <div className="gencrm_reports__filters">
          <label>De:</label>
          <input
            type="date"
            className="gencrm_app__filter-input"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
          />
          <label>Até:</label>
          <input
            type="date"
            className="gencrm_app__filter-input"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
          />
          <button className="gencrm_btn gencrm_btn--primary" onClick={load} disabled={loading}>
            {loading ? 'A carregar…' : 'Aplicar'}
          </button>
        </div>

        {error && <div className="gencrm_ws_error">Erro: {error}</div>}

        <ReportRenderer type={active} data={data} loading={loading} />
      </div>
    </div>
  );
};

const ReportRenderer = ({ type, data, loading }) => {
  if (loading) return <div className="gencrm_ws_loading">A carregar relatório…</div>;
  if (!data) return null;

  switch (type) {
    case 'kpis': return <KpisGrid data={data} />;
    case 'orders-timeline': return <TimelineChart data={Array.isArray(data) ? data : []} />;
    case 'orders-by-status': return <StatusTable data={Array.isArray(data) ? data : []} />;
    case 'top-customers': return <CustomersTable data={Array.isArray(data) ? data : []} />;
    case 'top-products': return <ProductsTable data={Array.isArray(data) ? data : []} />;
    case 'pendentes': return <PendentesTable data={Array.isArray(data) ? data : []} />;
    case 'sales-vs-sap': return <SalesVsSap data={data} />;
    case 'quotations': return <QuotationsTable data={Array.isArray(data) ? data : []} />;
    default: return null;
  }
};

const KpisGrid = ({ data }) => (
  <div className="gencrm_card">
    <h3>KPIs Gerais</h3>
    <div className="gencrm_card__grid">
      <div><strong>Total Encomendas:</strong><br />{num(data.total_orders)}</div>
      <div><strong>Receita:</strong><br />{eur(data.total_revenue)}</div>
      <div><strong>AOV:</strong><br />{eur(data.aov)}</div>
      <div><strong>Finalizadas:</strong><br />{num(data.done_count)}</div>
      <div><strong>Pendentes:</strong><br />{num(data.pending_count)}</div>
      <div><strong>Bloqueadas:</strong><br />{num(data.blocked_count)}</div>
      <div><strong>Aguarda Hospitalar:</strong><br />{num(data.waiting_hospitalar_count)}</div>
      <div><strong>Unidades vendidas:</strong><br />{num(data.total_units_sold)}</div>
    </div>
  </div>
);

const TimelineChart = ({ data }) => {
  if (!data.length) return <EmptyReport />;
  const max = Math.max(...data.map((d) => Number(d.revenue) || 0), 1);
  const width = Math.min(1200, data.length * 30);
  const height = 200;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * width;
    const y = height - (Number(d.revenue) / max) * (height - 20) - 10;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="gencrm_card">
      <h3>Timeline (Receita diária)</h3>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }}>
        <polyline points={points} fill="none" stroke="var(--primary, #00785f)" strokeWidth="2" />
        {data.map((d, i) => {
          const x = (i / (data.length - 1 || 1)) * width;
          const y = height - (Number(d.revenue) / max) * (height - 20) - 10;
          return <circle key={i} cx={x} cy={y} r="3" fill="var(--primary, #00785f)" />;
        })}
      </svg>
      <small style={{ color: '#666' }}>
        {data.length} dias · pico: {eur(max)} em {data.find((d) => Number(d.revenue) === max)?.date}
      </small>
    </div>
  );
};

const StatusTable = ({ data }) => {
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_rpt_status');
  const columns = useMemo(() => [
    { key: 'label_pt', label: 'Estado', width: 200 },
    { key: 'count', label: 'Count', width: 100, aggregate: 'sum', render: (r) => num(r.count) },
    { key: 'total', label: 'Total', width: 130, aggregate: 'sum', render: (r) => eur(r.total) },
    { key: 'dist', label: 'Distribuição', width: 220, hideable: false, render: (r) => {
      const max = Math.max(...data.map((d) => Number(d.count) || 0), 1);
      return (
        <div style={{ background: '#e5e7eb', width: 200, height: 12, borderRadius: 6 }}>
          <div style={{
            background: 'var(--primary, #00785f)', height: '100%',
            width: `${(r.count / max) * 100}%`, borderRadius: 6,
          }} />
        </div>
      );
    } },
  ], [data]);
  if (!data.length) return <EmptyReport />;
  return (
    <div className="gencrm_card">
      <h3>Distribuição por Estado</h3>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(r) => r.status_id}
        pageSize={50}
        density="compact"
        enableTotals
        emptyText="Sem dados."
        exportPrefix="gencrm_rpt_status"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

const CustomersTable = ({ data }) => {
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_rpt_customers');
  const columns = useMemo(() => [
    { key: 'rank', label: '#', width: 60, render: (c, i) => i + 1 },
    { key: 'customer_name', label: 'Cliente', width: 260, render: (c) => c.customer_name || '—' },
    { key: 'orders_count', label: 'Encomendas', width: 120, aggregate: 'sum', render: (c) => num(c.orders_count) },
    { key: 'total_revenue', label: 'Receita', width: 140, aggregate: 'sum', render: (c) => eur(c.total_revenue) },
    { key: 'aov', label: 'AOV', width: 120, render: (c) => eur(c.aov) },
  ], []);
  if (!data.length) return <EmptyReport />;
  return (
    <div className="gencrm_card">
      <h3>Top 20 Clientes</h3>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(c, i) => c.customer_id || i}
        pageSize={20}
        density="compact"
        enableTotals
        emptyText="Sem dados."
        exportPrefix="gencrm_rpt_customers"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

const ProductsTable = ({ data }) => {
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_rpt_products');
  const columns = useMemo(() => [
    { key: 'rank', label: '#', width: 60, render: (p, i) => i + 1 },
    { key: 'product_name', label: 'Produto', width: 260, render: (p) => p.product_name || '—' },
    { key: 'sap', label: 'SAP', width: 110, render: (p) => <code>{p.sap || '—'}</code> },
    { key: 'chnm_codigo', label: 'CHNM', width: 110, render: (p) => <code>{p.chnm_codigo || '—'}</code> },
    { key: 'total_qty', label: 'Qty', width: 90, aggregate: 'sum', render: (p) => num(p.total_qty) },
    { key: 'total_revenue', label: 'Receita', width: 140, aggregate: 'sum', render: (p) => eur(p.total_revenue) },
    { key: 'avg_price', label: 'Preço médio', width: 130, render: (p) => eur(p.avg_price) },
  ], []);
  if (!data.length) return <EmptyReport />;
  return (
    <div className="gencrm_card">
      <h3>Top 50 Produtos</h3>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(p, i) => p.catalog_id || i}
        pageSize={50}
        density="compact"
        enableTotals
        emptyText="Sem dados."
        exportPrefix="gencrm_rpt_products"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

const PendentesTable = ({ data }) => {
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_rpt_pendentes');
  const columns = useMemo(() => [
    { key: 'order_number', label: 'Ordem', width: 120, render: (p) => '#' + p.order_number },
    { key: 'customer_name', label: 'Cliente', width: 220 },
    { key: 'product_name', label: 'Produto', width: 260 },
    { key: 'quantity', label: 'Qty Pend', width: 100, aggregate: 'sum', render: (p) => num(p.quantity) },
    { key: 'rangel_stock', label: 'Stock Rangel', width: 120, render: (p) => (
      p.rangel_stock > 0
        ? <span className="gencrm_badge gencrm_badge--green">{num(p.rangel_stock)}</span>
        : <span className="gencrm_badge gencrm_badge--red">0</span>
    ) },
    { key: 'pending_reason', label: 'Motivo', width: 180, render: (p) => p.pending_reason || '—' },
  ], []);
  if (!data.length) return <EmptyReport />;
  return (
    <div className="gencrm_card">
      <h3>Pendentes ({data.length})</h3>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(p) => p.order_id + '_' + p.line_number}
        pageSize={50}
        density="compact"
        enableTotals
        emptyText="Sem pendentes."
        exportPrefix="gencrm_rpt_pendentes"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

const SalesVsSap = ({ data }) => (
  <div className="gencrm_card">
    <h3>Sales vs SAP</h3>
    <div className="gencrm_card__grid">
      <div><strong>Enviadas SAP:</strong><br />{num(data.orders_sent_to_sap)}</div>
      <div><strong>Receita enviada:</strong><br />{eur(data.revenue_sent)}</div>
      <div><strong>Finalizadas:</strong><br />{num(data.orders_completed)}</div>
      <div><strong>Receita faturada:</strong><br />{eur(data.revenue_invoiced)}</div>
      <div><strong>Total faturas:</strong><br />{num(data.total_invoices)}</div>
      <div><strong>Valor faturado:</strong><br />{eur(data.total_invoiced_value)}</div>
    </div>
  </div>
);

const QuotationsTable = ({ data }) => {
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_rpt_quotations');
  const columns = useMemo(() => [
    { key: 'quotation_name', label: 'Cotação', width: 180, render: (q) => q.quotation_name || q.quotation_sap },
    { key: 'customer_name', label: 'Cliente', width: 200, render: (q) => q.customer_name || '—' },
    { key: 'valid_until', label: 'Válido até', width: 120, render: (q) => q.valid_until ? new Date(q.valid_until).toLocaleDateString('pt-PT') : '—' },
    { key: 'total_available', label: 'Disponível', width: 110, aggregate: 'sum', render: (q) => num(q.total_available) },
    { key: 'total_consumed', label: 'Consumido', width: 110, aggregate: 'sum', render: (q) => num(q.total_consumed) },
    { key: 'consumption_pct', label: '%', width: 80, render: (q) => ((q.consumption_pct || 0) * 100).toFixed(1) + '%' },
    { key: 'days_until_expiration', label: 'Dias expirar', width: 100 },
  ], []);
  if (!data.length) return <EmptyReport />;
  return (
    <div className="gencrm_card">
      <h3>Cotações (Lisboa Central/Norte)</h3>
      <DataTable
        columns={columns}
        data={data}
        rowKey={(q) => q.quotation_id}
        pageSize={50}
        density="compact"
        enableTotals
        emptyText="Sem dados."
        exportPrefix="gencrm_rpt_quotations"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

const EmptyReport = () => (
  <div className="gencrm_card">
    <p className="gencrm_ws_empty">Sem dados para o período selecionado.</p>
  </div>
);

export default Reports;
